import { useCallback, useMemo } from 'react'

import { ChildOrg, OrgByParent } from 'api/types/orgsApiTypes'
import { useOrgsByParent } from 'hooks/data/orgsHook'
import { useDynamicLabels } from 'layouts/app/useDynamicLabels'

export const ALL_ENTITIES_VALUE = '__all__'

export type EntityOption = {
  orgName: string
  displayName: string | null
  orgSlug: string | null | undefined
}

export type Entities = EntityOption[]

export const useOrgLegalEntities = (orgId?: string) => {
  const { data, isLoading } = useOrgsByParent()
  const { allLegalEntitiesLabel } = useDynamicLabels()

  const findParentOrg = useCallback(
    (entityId: string) => {
      if (data === undefined) {
        return undefined
      }

      return data.find((org) => org.children.some((entity) => entity.org_name === entityId))
        ?.org_name
    },
    [data]
  )

  const findOrgEntities = useCallback(
    (targetOrgId: string | undefined): Entities => {
      if (!data || !targetOrgId) {
        return []
      }

      const matchingOrg: OrgByParent | undefined = data.find((org) => org.org_name === targetOrgId)
      const orgEntities: ChildOrg[] = matchingOrg?.children || []

      if (orgEntities.length > 0) {
        const entities: Entities = []
        // If the org has entities and the user has access to the parent org, add the "All" option at the start
        if (matchingOrg?.user_has_access) {
          entities.push({
            orgName: ALL_ENTITIES_VALUE,
            displayName: allLegalEntitiesLabel,
            orgSlug: undefined,
          })
        }
        entities.push(
          ...orgEntities.map(
            (entity): EntityOption => ({
              orgName: entity.org_name,
              displayName: entity.org_display_name ?? null,
              orgSlug: entity.org_slug,
            })
          )
        )
        return entities
      }

      return []
    },
    [allLegalEntitiesLabel, data]
  )

  const hasEntities = useCallback(
    (targetOrgId: string) => {
      if (data === undefined) {
        return false
      }

      const matchingOrg: OrgByParent | undefined = data.find((org) => org.org_name === targetOrgId)
      return (matchingOrg?.children.length || 0) > 0
    },
    [data]
  )

  const entities = useMemo(() => {
    return findOrgEntities(orgId)
  }, [findOrgEntities, orgId])

  return {
    entities,
    isLoading,
    findParentOrg,
    hasEntities,
  }
}
